import { useState, useRef, useEffect } from 'react';
import { useAppDispatch } from '../../../store/hooks';
import { requestAudio } from '../../../api_request/api_request';

/**
 * This variable determines whether an Audio element from any SpeakPlaylistManager component is playing.
 * That way, we can stop it when another starts.
 */
let currentlyPlayingAudio = null as HTMLAudioElement | null;

/**
 * Map of all audios created so far since this module loaded, so if the user browses away and comes back, we can reuse them.
 */
const allAudios = {} as {[endpoint: string]: HTMLAudioElement};

/**
 * This element accepts an array of file paths. Each one is a portion of a sentence to be spoken, downloaded from Google Translate.
 * It doesn't let me speak more than 100 characters at a time, so we have to download the sentence in snippets.
 * 
 * This object converts all the snippets to HTMLAudioElements (not in DOM) and creates a GUI that controls them as a unit.
 */
export function SpeakPlaylistManager(props: {fileEndpoints: string[]}) 
{
    const appDispatch = useAppDispatch();

    const fileEndpoints = props.fileEndpoints;

    // Use a ref to store the array of HTMLAudioElement references
    const audioElementsRef = useRef<HTMLAudioElement[]>([]);

    //The index in audioElementsRef that should be playing.
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    //The time, relative to the entire playlist.
    const [currentTime, setCurrentTime] = useState<number>(0);
    //Combined time of all files.
    const [playlistDuration, setPlaylistDuration] = useState<number>(0);
    
    //'none': not playing; 'waiting': will start playback when data loads; 'started': now we're talking.
    type PlaybackState = 'paused'|'waiting'|'started';
    const [playbackState, setPlaybackState] = useState<PlaybackState>('paused');

    //Whether the list can be played. 'none': no data, etc.
    type PlaylistDataState = 'none'|'loading'|'loaded'|'error';
    const [playlistDataState, setPlaylistDataState] = useState<PlaylistDataState>('none');

    // Function to create and return an array of audio elements
    function createAudioElements()
    {
        //Don't create elements again!
        if (audioElementsRef.current.length)
        {
            return;
        }

        audioElementsRef.current = fileEndpoints.map(function(endpoint, i)
        {
            const audio = requestAudio(endpoint, appDispatch, allAudios[endpoint]);
            audio.setAttributeNS('ernots', 'index', i.toString());
            allAudios[endpoint] = audio;
            
            audio.onplay = function()
            {
                if (currentlyPlayingAudio)
                {
                    //Stop the previous audio.
                    currentlyPlayingAudio.pause();
                    //Also reset its time.
                    currentlyPlayingAudio.currentTime = 0;
                }

                //And of course.
                currentlyPlayingAudio = audio;
                setPlaybackState('started');
            };

            audio.onended = function()
            {
                currentlyPlayingAudio = null;

                const next = audioElementsRef.current?.[i + 1];
                if (next)
                {
                    //Start the next one.
                    next.play();
                }
                else
                {
                    //Reset.
                    setCurrentIndex(0);
                    setCurrentTime(0);
                }
            };
            
            audio.ondurationchange = function()
            {
                recomputePlaylistState();
            };

            audio.ontimeupdate = function()
            {
                const time = convertToPlaylistTime(i, audio.currentTime);

                if (!audio.paused && !audio.ended)
                {
                    setCurrentTime(time);
                    setCurrentIndex(i);
                    setPlaybackState('started');
                }
            };

            audio.onpause = function()
            {
                if (audio === currentlyPlayingAudio)
                {
                    currentlyPlayingAudio = null;
                }

                setPlaybackState('paused');
            }

            audio.onerror = function()
            {
                recomputePlaylistState();
            };
            
            return audio;
        });
    };

    function startPlayback()
    {
        createAudioElements();
        setPlaybackState('waiting');
    }

    function stopPlayback()
    {
        for (const a of audioElementsRef.current)
        {
            a.pause();
        }
    }

    function retryDownload()
    {
        for (const a of audioElementsRef.current)
        {
            if (a.getAttributeNS('ernots', 'error'))
            {
                const endpoint = a.getAttributeNS('ernots', 'endpoint') || '';
                requestAudio(endpoint, appDispatch, a);
            }
        }
    }

    /** From a file index and a timestamp, compute the timestamp relative to the playlist. */
    function convertToPlaylistTime(fileIndex: number, timeInFile: number)
    {
        //Sum the durations of the files that precede the current one.
        const totalTimeBeforeFile = audioElementsRef.current
            .slice(0, fileIndex)
            .reduce((total, audio) => total + audio.duration, 0);

        // Add the time within the current file.
        return totalTimeBeforeFile + timeInFile;
    }

    /** From a playlist time, compute the file that we need to be playing. */
    function convertToFileTime(timeInPlaylist: number)
    {
        let accumulatedTime = 0;
    
        // Find the file that contains the time in the playlist.
        for (let i = 0; i < audioElementsRef.current.length; i++)
        {
            const audio = audioElementsRef.current[i];
            
            if (accumulatedTime + audio.duration >= timeInPlaylist)
            {
                // The time falls within this file.
                return { fileIndex: i, timeInFile: timeInPlaylist - accumulatedTime };
            }
            
            accumulatedTime += audio.duration;
        }

        //If there are no audio elements, return null.
        if (!audioElementsRef.current.length)
        {
            return null;
        }
        
        // If timeInPlaylist exceeds the total runtime, return the last file and its full duration.
        return {
            fileIndex: audioElementsRef.current.length - 1,
            timeInFile: audioElementsRef.current[audioElementsRef.current.length - 1].duration,
        };
    }

    /** Use convertToFileTime, then move the cursor to the selected file. */
    function goToPlaylistTime(timeInPlaylist: number)
    {
        const ft = convertToFileTime(timeInPlaylist);

        //If there are no audio elements, return null.
        if (!ft)
        {
            return null;
        }

        const audio = audioElementsRef.current[ft.fileIndex];
        audio.currentTime = ft.timeInFile;

        if (playbackState === 'started')
        {
            audio.play();
        }
    }

    /**
     * Change the playlist state.
     * None if there are no files to play.
     * Loading if ANY file doesn't have enough data to play through the end.
     * Error if there are errors
     * Loaded if we're ready to rumble.
     * 
     * Along with that, the function also adds up all the durations into a playlist duration.
     */
    function recomputePlaylistState()
    {
        let totalDuration = 0;
        let loading = false;
        let error = false;
        if (!audioElementsRef.current.length)
        {
            setPlaylistDataState('none');
            return;
        }

        for (var a of audioElementsRef.current)
        {
            if (a.getAttributeNS('ernots', 'error')) error = true;
            else if (!a.getAttributeNS('ernots', 'ready')) loading = true;
            else totalDuration += a.duration;
        }
        
        if (error)
        {
            setPlaylistDataState('error');
        }
        else if (loading)
        {
            setPlaylistDataState('loading');
        }
        else
        {
            setPlaylistDataState('loaded');
            setPlaylistDuration(totalDuration);
        }
    }

    function handleBack() 
    {
        goToPlaylistTime(currentTime - 5);
    }

    function handleForward() 
    {
        goToPlaylistTime(currentTime + 5);
    }

    function handleSpeedChange(change: number) 
    {
        for (const a of audioElementsRef.current)
        {
            try 
            {
                a.playbackRate *= change;
            } catch {}
        }
    }

    /** If we were waiting for data to load and it just did, start playback. */
    useEffect(function()
    {
        if (playbackState === 'waiting' && playlistDataState === 'loaded')
        {
            audioElementsRef.current[currentIndex].play();
        }
    }, [playbackState, playlistDataState, currentIndex]);

    /**
     * Google Translate has a quirk. When it creates a speech file, it leaves a bit of silence at the end.
     * This silence can be annoying when we break a sentence into segments of 100 characters.
     * So I just fast-forward to the end. The next segment should then start playing.
     */
    useEffect(function()
    {
        if (playbackState === 'started' && currentlyPlayingAudio) 
        {
            const cpad = currentlyPlayingAudio.duration;
            const cpact = currentlyPlayingAudio.currentTime;
            if ((cpact / cpad) > 0.9)
            {
                currentlyPlayingAudio.currentTime = cpad;
            }
        }
    });

    /** When this component is removed from the page, stop playback. */
    useEffect(function()
    {
        return function()
        {
            stopPlayback();
        }
    }, []);

    const playbackDisabled = playlistDataState === 'error';

    let playPauseButton = <button onClick={() => stopPlayback()} disabled={playbackDisabled}>⏸️</button>; //Start playback.
    if (playbackState === 'paused')
    {
        playPauseButton = <button onClick={() => startPlayback()} disabled={playbackDisabled}>▶️</button> //Pause playback.
    }

    let statusLabel = <></>;
    if (playlistDataState === 'error')
    {
        statusLabel = <button onClick={() => retryDownload()}>❗Retry download❗</button>; //Retry failed downloads.
    }
    else if (playlistDataState === 'loading')
    {
        statusLabel = <span>⏳Loading⏳</span>
    }

    return (
        <div>
            {/* Slider for entire playlist */}
            <input
                type="range"
                min="0"
                max={playlistDuration * 1000}
                value={currentTime * 1000}
                onChange={e => goToPlaylistTime(e.target.valueAsNumber / 1000)}
                style={{ width: '100%' }}
                disabled={playbackDisabled}
            />

            <div>
                <button onClick={handleBack} disabled={playbackDisabled}>⏪</button>{/* Back 5s */}
                {playPauseButton}
                <button onClick={handleForward} disabled={playbackDisabled}>⏩</button>{/* Forward 5s */} 

                {/* Change playback speed */}
                <span>Speed: </span>
                <button onClick={() => handleSpeedChange(1 / 1.1)} disabled={playbackDisabled}>−10%</button>
                <button onClick={() => handleSpeedChange(1.1)} disabled={playbackDisabled}>+10%</button>
                {statusLabel}
            </div>
        </div>
    );
}

/**
 * Split the string into segments of at most 100 characters, which is the limit for Google Translate.
 */
export function createSegments(text: string)
{
    var words = text.split(' ');
    var segments = [];
    var bound = 100;
        
    var segment = '';
    for (var word of words)
    {
        //The segment with the next word
        var segment2 = segment + word + ' ';

        if (segment2.length < bound)
        {
            //If segment2 is less than 100 chars, keep going.
            segment = segment2;
        }
        else
        {
            //If segment2 is too big, push segment and add the current word to the next iteration.
            //This also happens if a single word is somehow over 100 chars.
            segments.push(segment.trim());
            segment = word + ' ';
        }
    }

    //This handles any leftovers.
    if (segment.length > 0)
    {
        segments.push(segment.trim());
    }

    return segments;
}

export function createSegmentEndpoint(segment: string, language: string)
{
    return `download_gtrans/?action=speak&tl=${language}&q=${encodeURIComponent(segment)}`
}